import { useState, useMemo, useCallback } from "react";
import styled from "styled-components";
import { pick } from "lodash-es";
import { encode } from "base64-arraybuffer";

import { Upload, Button } from "antd";
import { UploadOutlined, PaperClipOutlined, LoadingOutlined, DeleteTwoTone } from "@ant-design/icons";

// Custom field representing a list of uploaded files.
export function FileUploader(props) {
    const {
        formData: originalFiles = [],
        onChange,
        uiSchema,
        registry
    } = props;
    
    const [uploading, setUploading] = useState(false);
    const [files, setFiles] = useState(originalFiles);
    
    const beforeUpload = (file) => {
        const reader = new FileReader();
        reader.addEventListener("load", (e) => {
            // Transform ArrayBuffer to base64 string
            file.content = encode(e.target.result);
            const newFiles = [...files, pick(file, ["content", "lastModified", "name", "size", "type"])];
            setFiles(newFiles);
            onChange(newFiles); // Set form data for upload
            setUploading(false);
        });
        setUploading(true);
        reader.readAsArrayBuffer(file);
        
        return false; // Don't let Upload attempt to upload the file
    };
    
    const onRemove = useCallback((file) => {
        const newFiles = [...files];
        newFiles.splice(files.indexOf(file), 1);
        setFiles(newFiles);
        onChange(newFiles); // Set form data for upload
    }, [files, onChange]);
    
    const fileRows = useMemo(() => files.map((file) => {
        let downloadElement;
        if (file.url || file.content) {
            // Ideally API should return a URL for each attachment.
            // If it instead returns the content of all attachments, allow download anyway (assuming plaintext for files with no specified MIME-type).
            downloadElement = <a download={file.name} href={file.url || window.URL.createObjectURL(new Blob([file.content], { type: file.type?.length ? file.type : "text/plain" }))}>{file.name}</a>;
        } else {
            // We don't have a URL or the content of the file, so there is no way to download it.
            downloadElement = file.name;
        }
        return (
            <FileRow key={file.name}>
                <PaperClipIcon/>
                {downloadElement}
                <DeleteIcon twoToneColor="#c4c4c4" onClick={() => onRemove(file)}/>
            </FileRow>
        );
    }), [files, onRemove]);
    
    return (
        <div>
            {registry.fields.TitleField({ id: "file_uploader", title: uiSchema["ui:title"] || "Files" })}
            <Upload
                beforeUpload={beforeUpload}
                onRemove={onRemove}
                showUploadList={false}
            >
                <Button disabled={uploading} icon={uploading ? <LoadingOutlined/> : <UploadOutlined/>}>Upload</Button>
            </Upload>
            {fileRows}
        </div>
    );
}

const FileRow = styled.div`
    width: 100%;
    display: flex;
    border-bottom: 1px dashed #c4c4c4;
    margin-top: 3px;
`;

const PaperClipIcon = styled(PaperClipOutlined)`
    margin-right: 10px;
    color: #c4c4c4;
`;

const DeleteIcon = styled(DeleteTwoTone)`
    margin-left: auto;

    &:hover {
        filter: brightness(0.9);
    }
`;
